import type { ReactNode } from 'react';

/**
 * The login shell every Crystal app shares.
 *
 * Split screen: the form on the left on a clean white field, an editorial art
 * panel on the right that carries the realm's identity (colour, product name,
 * what the app is for). Below `lg` the art panel drops away and the form
 * centres — a phone gets the form, not the decoration.
 *
 * Each realm passes its own accent and copy; the SHAPE never changes. That's
 * the point: Spares, Containers, Purchase and Porta should feel like four doors
 * into one building, not four different buildings.
 *
 * Purely presentational. It owns no auth state and makes no network calls —
 * every page keeps its own submit logic exactly as it was.
 */

export type Realm = 'spares' | 'containers' | 'purchase' | 'porta';

interface RealmTheme {
  /** Tailwind gradient stops for the art panel. */
  gradient: string;
  /** Accent used by the submit button + focus rings. */
  accent: string;
  accentHover: string;
  ring: string;
  text: string;
  softBg: string;
}

/**
 * ONE brand across every realm — Crystal navy with an amber accent.
 *
 * The realms used to each carry their own hue (blue / indigo / teal /
 * orange), which is precisely what made four doors look like four
 * buildings. The design system has a single accent, so every realm now
 * resolves to it. The per-realm keys stay so no call site changes; they
 * simply all point at the brand.
 */
const CRYSTAL: RealmTheme = {
  gradient: 'from-navy-600 via-navy-700 to-navy-900',
  accent: 'bg-navy-600',
  accentHover: 'hover:bg-navy-700',
  ring: 'focus:ring-navy-100 focus:border-navy-600',
  text: 'text-navy-600',
  softBg: 'bg-navy-50',
};

export const REALM_THEME: Record<Realm, RealmTheme> = {
  spares: CRYSTAL,
  containers: CRYSTAL,
  purchase: CRYSTAL,
  porta: CRYSTAL,
};

export default function AuthShell({
  realm,
  title,
  tagline,
  /** 3-4 short lines describing what this app does — the art panel's body. */
  highlights,
  /** The realm switcher / product tabs, rendered above the form. Optional. */
  tabs,
  children,
  footer,
}: {
  realm: Realm;
  title: string;
  tagline: string;
  highlights?: string[];
  tabs?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const t = REALM_THEME[realm];

  return (
    <div className="auth-shell bg-white">
      {/* ---------------- Form side ---------------- */}
      <div className="auth-form">
        <div className="mx-auto w-full max-w-sm">
          <div className="animate-fade-up">
            <img
              src="/crystal-logo-black.webp"
              alt="Crystal Cold Chain Solution Company"
              className="h-12 w-auto object-contain"
            />
          </div>

          {tabs && <div className="mt-8 animate-fade-up">{tabs}</div>}

          <div className="mt-8 animate-fade-up" style={{ animationDelay: '60ms' }}>
            {/* DS §Hero: Manrope 800, -1px tracking. */}
            <h1 className="font-display text-2xl font-extrabold tracking-[-0.5px] text-slate-950 sm:text-3xl">
              {title}
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">{tagline}</p>
          </div>

          <div className="mt-7 animate-fade-up" style={{ animationDelay: '120ms' }}>
            {children}
          </div>

          {footer && (
            <div className="mt-6 animate-fade-up" style={{ animationDelay: '180ms' }}>
              {footer}
            </div>
          )}

          <p className="mt-10 text-[11px] text-slate-400">
            © {new Date().getFullYear()} Crystal Group · Internal system. Authorised users only.
          </p>
        </div>
      </div>

      {/* ---------------- Art side ---------------- */}
      <div className={`auth-art bg-gradient-to-br ${t.gradient}`}>
        {/* Drifting light orbs — slow enough to read as atmosphere, not motion. */}
        <div className="pointer-events-none absolute -left-24 top-1/4 h-96 w-96 animate-orb-drift rounded-full bg-white/20 blur-3xl" />
        <div
          className="pointer-events-none absolute -right-16 bottom-8 h-[28rem] w-[28rem] animate-orb-drift rounded-full bg-white/10 blur-3xl"
          style={{ animationDelay: '-6s' }}
        />
        {/* Fine grid — reads as "system", and gives the flat gradient a surface. */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at 60% 40%, black, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 60% 40%, black, transparent 75%)',
          }}
        />

        <div className="relative flex h-full flex-col justify-between p-12 xl:p-16">
          {/* DS §Hero eyebrow: JetBrains Mono, amber, 2.4px tracking. */}
          <div className="animate-fade-in font-mono text-[10.5px] uppercase tracking-[2.4px] text-amber-500">
            Crystal Group
          </div>

          <div>
            <h2
              className="max-w-md animate-fade-up font-display text-4xl font-extrabold leading-[1.08] tracking-[-1px] text-white xl:text-5xl"
              style={{ animationDelay: '80ms' }}
            >
              {tagline}
            </h2>

            {highlights && highlights.length > 0 && (
              <ul className="mt-8 space-y-2.5">
                {highlights.map((h, i) => (
                  <li
                    key={h}
                    className="animate-slide-in-right glass flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-white/90"
                    style={{ animationDelay: `${180 + i * 90}ms` }}
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    {h}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div
            className="animate-fade-in text-xs text-white/50"
            style={{ animationDelay: '400ms' }}
          >
            {title}
          </div>
        </div>
      </div>
    </div>
  );
}
